'use client';

// Global
import type { SearchResultsStoreState } from '@sitecore-search/react';
import { WidgetDataType, widget } from '@sitecore-search/react';
import { useState, useRef, useEffect } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { Splide as SplideCore } from '@splidejs/splide';
import '@splidejs/splide/css';
import type { ComponentParams, Field, ImageField, LinkField } from '@sitecore-content-sdk/nextjs';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { tv } from 'tailwind-variants';
import {
  Context as CloudSearchContext,
  getWidgetData,
  SearchWidgetItem,
  ComparisonFilter,
  WidgetRequestData,
  LogicalFilter,
} from '@sitecore-cloudsdk/search/browser';

// Local
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { XceleratePage } from 'src/baseTypes/Xcelerate.HztlFoundation.model';
import useIsEditing from 'lib/hooks/useIsEditing';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { Themes } from 'helpers/Constants/Constant';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

type NewsModel = {
  name?: string;
  image_url?: string;
  type?: string;
  pageTitle?: {
    jsonValue?: Field<string>;
  };
  template?: {
    id?: string;
    name?: string;
  };
  url?:
    | string
    | {
        url?: string;
      };
  image?: {
    jsonValue?: ImageField;
  };
  openGraphImageUrl?: {
    jsonValue?: ImageField;
  };
};

type SearchResultsProps = {
  defaultSortType?: SearchResultsStoreState['sortType'];
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  title?: Field<string>;
  description?: Field<string>;
  link?: LinkField;
  rfkId?: string;
  params?: ComponentParams;
  uid?: string;
  id?: string;
  latestArticles?: NewsModel[];
};

export const LatestArticleCardsSearchComponent = ({
  defaultItemsPerPage = 2,
  title,
  description,
  link,
  rfkId,
  params,
  uid,
  id,
  latestArticles,
}: SearchResultsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const currentPage = sitecoreContext?.route as XceleratePage & {
    itemId?: string;
  };
  const articleCardsRef = useRef<HTMLDivElement>(null);
  const theme = params?.selectTheme as Themes;

  const defaultSiteLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en-US';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [news, setNews] = useState<NewsModel[]>([]);
  const isEditing = useIsEditing();
  const [isDataAvailable, setIsDataAvailable] = useState<boolean>(false);
  const { isSearchInitialized } = useSearchInitialization();
  const { getDictionaryValue } = useDictionary();
  const isTablet = useIsTablet();
  const splideRef = useRef<SplideCore | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);

  const fetchNews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const rfk = rfkId || 'rfkid_reports_insights';

      // Build locale context
      const locale = defaultSiteLocale;
      const [language, countryRaw] = locale.split('-');
      const country = (countryRaw || 'US').toLowerCase();
      const ctx = new CloudSearchContext({
        locale: {
          language: (language || 'en').toLowerCase(),
          country,
        },
      });

      const searchSourceIds = process.env.NEXT_PUBLIC_SEARCH_NEWS_SOURCE_ID;

      // Define the filter to match key_contact_id with the route item ID
      const contactIdFilter = new ComparisonFilter('key_contact_id', 'eq', currentPage?.itemId);

      const typePressReleaseFilter = new ComparisonFilter('type', 'eq', 'Press Release');

      const PressReleaseFilter = new LogicalFilter('and', [
        contactIdFilter,
        typePressReleaseFilter,
      ]);

      const authorsFilter = new ComparisonFilter('authors', 'eq', currentPage?.itemId);

      const typeExpertCommentFilter = new ComparisonFilter('type', 'eq', 'Expert Comment');

      const ExpertCommentFilter = new LogicalFilter('and', [
        authorsFilter,
        typeExpertCommentFilter,
      ]);

      const widgetItem = new SearchWidgetItem(
        'newsarticle',
        rfk,
        {
          limit: defaultItemsPerPage || 2,
          content: {},
          filter: new LogicalFilter('or', [PressReleaseFilter, ExpertCommentFilter]),
        },
        searchSourceIds ? [searchSourceIds] : undefined
      );
      const response = await getWidgetData(new WidgetRequestData([widgetItem]), ctx);
      const content = response?.widgets?.[0]?.content as NewsModel[] | undefined;
      const available = !isEditing && (content?.length ?? 0) > 0;
      setIsDataAvailable(available);
      setNews(content || []);
    } catch {
      setError('Failed to load news');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }

    if (latestArticles && latestArticles.length > 0) {
      setNews(latestArticles?.slice(0, defaultItemsPerPage || 2) || []);
      const available = !isEditing && (latestArticles?.length ?? 0) > 0;
      setIsDataAvailable(available);
      setIsLoading(false);
      setError(null);
    } else {
      // Only run client-side
      if (typeof window !== 'undefined') {
        fetchNews();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfkId, defaultSiteLocale, defaultItemsPerPage, isSearchInitialized]);

  useScrollElementIntoView(articleCardsRef.current, {
    scrollTargetId: 'search-result-cards',
  });

  const { RenderingIdentifier } = params || {};

  const hasContent = Boolean(title?.value || description?.value);
  const hasCtas = Boolean(link?.value?.href);
  const styles = parseStyleParams(params as ComponentParams, ['cta1']);

  const {
    base,
    sectionTitle,
    sectionDescription,
    sectionContentWrapper,
    sectionTitleWrapper,
    sectionDivider,
    buttonWrapper,
    sectionWrapper,
    cardBase,
    card,
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow: eyebrowTV,
    heading: headingTV,
    footer,
    cta,
    imageWrapper,
    cardItemSectionDivider,
    containerCarousel,
    customPaginationDotsWrapper,
    paginationDots,
    dotStyle,
  } = TAILWIND_VARIANTS({ colCount: defaultItemsPerPage.toString() as '1' | '2' | '3' | '4' || '2' });

  const renderArticleCard = (n: NewsModel, index: number) => {
    const articleType = n?.type;
    const articleTemplateName = n?.template?.name;
    const articleTypeText =
      articleTemplateName === 'Expert Comment Page' || articleType === 'Expert Comment'
        ? `${getDictionaryValue('ExpertComment') || 'EXPERT COMMENT'}`
        : `${getDictionaryValue('PressRelease') || 'PRESS RELEASE'}`;
    const articleTitle = n?.pageTitle?.jsonValue || { value: n?.name };
    const articleImage = n?.image?.jsonValue ||
      n?.openGraphImageUrl?.jsonValue || {
        value: {
          src: n?.image_url,
          alt: n?.name,
          title: n?.name,
        },
      };
    const articleLink = typeof n?.url === 'string' ? n.url : n?.url?.url;

    return (
      <article
        className={container()}
        data-component="authorable/shared/lists/carditem"
        {...editingHydrationProps(isEditing)}
        id={RenderingIdentifier}
        {...getTestProps(`card-item-${index}`)}
      >
        <div className={header()}>
          {articleImage?.value?.src ? (
            <ImageWrapper
              field={articleImage}
              className={imageWrapper()}
              {...getTestProps(`image`)}
            />
          ) : (
              <ImageWrapper
                field={{
                  value: {
                    src: '/assets/profile_placeholder.png',
                    alt: 'No image',
                  },
                }}
                className={imageWrapper()}
                {...getTestProps(`related-profile-image-placeholder`)}
              />
          )}
        </div>
        <div className={body()}>
          <div className={contentWrapper()}>
            <div className={content()}>
              <div className={headingWithEyebrow()}>
                <PlainTextWrapper
                  className={eyebrowTV()}
                  editable
                  field={{ value: articleTypeText }}
                  tag="div"
                  {...getTestProps(`eyebrow`)}
                />
                <PlainTextWrapper
                  className={headingTV()}
                  field={articleTitle}
                  {...getTestProps(`heading`)}
                />
              </div>
              {n?.url ? <span className={cardItemSectionDivider()}></span> : null}
            </div>
            <div className={footer()}>
              <LinkWrapper
                ctaComponentClass="component-card-item-button-1-color"
                className={cta()}
                ctaStyle={getCtaStyle(styles.cta1, 'secondary')}
                field={{
                  text: `${getDictionaryValue('ReadMore') || 'Read more'}`,
                  href: articleLink as string,
                }}
                ctaIcon="arrow-right"
                {...getTestProps(`primary-cta`)}
                gtmEvent={createCTAClickGTMEvent({
                  header: articleTitle?.value,
                  ctaText: `${getDictionaryValue('ReadMore') || 'Read more'}`,
                  ctaType: 'blue',
                  componentName: 'Card Set',
                  ctaLink: articleLink as string,
                })}
              />
            </div>
          </div>
        </div>
      </article>
    );
  };

  return isDataAvailable ? (
    <section
      id={id}
      data-component="authorable/shared/search/ExpertCommentsOnNews"
      {...editingHydrationProps(isEditing)}
      {...getTestProps(`component-search-result-${uid}`)}
      className={sectionWrapper()}
    >
      <BrandAndThemeProvider theme={theme}>
        <div className={base()}>
          <div className={sectionContentWrapper({ hasContent })}>
            <div className={sectionTitleWrapper()}>
              {title?.value?.trim() && (
                <PlainTextWrapper className={sectionTitle()} field={title} tag="h2" />
              )}
              <RichTextWrapper className={sectionDescription()} field={description} />
              {hasContent ? <span className={sectionDivider()}></span> : null}
            </div>
            {isLoading && (
              <ul className="text-center">
                <li key="loading">
                  <span>Loading…</span>
                </li>
              </ul>
            )}
            {!isLoading && error && (
              <ul className="text-center">
                <li key="error">
                  <span>{error}</span>
                </li>
              </ul>
            )}
            {!isLoading &&
              !error &&
              news?.length > 0 &&
              (isTablet ? (
                <div className={containerCarousel()}>
                  <Splide
                    ref={(ref) => {
                      if (ref?.splide) {
                        splideRef.current = ref.splide as unknown as SplideCore;
                      }
                    }}
                    hasTrack={false}
                    onMounted={(splide) => setTotalSlides(splide.length)}
                    onMove={(_splide, newIndex) => setCurrentIndex(newIndex)}
                    onMoved={(splide) => setTotalSlides(splide.length)}
                    options={{
                      arrows: false,
                      gap: 'var(--layout-base-gutter)',
                      pagination: false,
                      perMove: 1,
                      perPage: 1,
                      rewind: true,
                      speed: 500,
                      type: 'slide',
                      width: '100%',
                      drag: true,
                    }}
                  >
                    <SplideTrack className="!overflow-visible">
                      {news.map((n, index) => (
                        <SplideSlide
                          key={index}
                          tabIndex={index !== currentIndex ? -1 : 0}
                          aria-hidden={index !== currentIndex}
                          className={card()}
                          {...getTestProps(`card-${index}`)}
                        >
                          {renderArticleCard(n, index)}
                        </SplideSlide>
                      ))}
                    </SplideTrack>
                  </Splide>
                  {news?.length > 1 && (
                    <nav role="navigation" aria-label="Carousel Pagination">
                      <ul className={customPaginationDotsWrapper()}>
                        {Array.from({ length: totalSlides }).map((_, i) => (
                          <li key={i}>
                            <button
                              onClick={() => splideRef.current?.go(i)}
                              className={paginationDots({ isActiveSlide: i === currentIndex })}
                              aria-label={`Go to slide ${i + 1}`}
                            >
                              <SvgIcon
                                icon={
                                  i === currentIndex ? 'carousel-active-pagedot' : 'carousel-pagedot'
                                }
                                size={i === currentIndex ? 'm' : 'xxs'}
                                viewBox={i === currentIndex ? '0 0 40 12' : '0 0 12 12'}
                                className={dotStyle({ isActiveSlide: i === currentIndex })}
                              />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}
                </div>
              ) : (
                <ul className={cardBase()} aria-label="List of cards">
                  {news.map((n, index) => (
                    <li
                      className={card()}
                      key={index}
                      role="listitem"
                      {...getTestProps(`card-${index}`)}
                    >
                      {renderArticleCard(n, index)}
                    </li>
                  ))}
                </ul>
              ))}
            {hasCtas && (
              <div className={buttonWrapper()}>
                <LinkWrapper
                  ctaComponentClass="component-card-item-button-2-color"
                  ctaStyle={getCtaStyle(styles.cta1, 'primary')}
                  field={link}
                  ctaIcon="arrow-right"
                  {...getTestProps(`secondary-cta`)}
                  gtmEvent={createCTAClickGTMEvent({
                    header: title?.value,
                    ctaText: link?.value?.text,
                    ctaType: 'blue',
                    componentName: 'LatestArticleCards',
                    ctaLink: link?.value?.href,
                  })}
                />
              </div>
            )}
          </div>
        </div>
      </BrandAndThemeProvider>
    </section>
  ) : (
    <></>
  );
};

const LatestArticleCardsSearchWidget = widget(
  LatestArticleCardsSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'newsarticle'
);

export default LatestArticleCardsSearchWidget;

const TAILWIND_VARIANTS = tv({
  slots: {
    sectionWrapper: ['scroll-mt-[64px]', 'md:scroll-mt-0'],
    base: [
      'flex',
      'bg-component-section-bg',
      'gap-component-section-container-gutter',
      'flex-wrap',
      'relative',
    ],
    sectionTitleWrapper: [
      'flex',
      'flex-col',
      'gap-space-between-base',
      'max-w-columns-two-third-max-width',
    ],
    sectionTitle: [
      'text-component-section-title',
      'font-typography-font-family-header',
      'text-typography-font-size-xlarge',
      'leading-typography-line-height-xlarge',
    ],
    sectionDescription: [
      'text-component-section-body',
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
    ],
    sectionContentWrapper: ['flex', 'flex-col', 'w-full', 'h-auto'],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    buttonWrapper: ['flex', 'gap-spacing-spacing-16', 'flex-col', 'md:flex-row'],
    cardBase: ['gap-layout-base-gutter', 'flex', 'flex-wrap', 'justify-center'],
    card: ['flex', 'flex-col', 'p-[8px]', 'lg:p-0'],
    container: [
      'flex',
      'w-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'h-full md:h-auto',
      'overflow-hidden',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
    ],
    body: ['flex', 'flex-col', 'w-full', 'items-center'],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24'],
    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    eyebrow: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'font-normal',
      'leading-[150%]',
    ],
    eyebrowCategory: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'font-normal',
      'leading-[150%]',
      'uppercase',
    ],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-small',
      'text-component-card-item-title',
      'leading-typography-line-height-small',
    ],
    cardItemSectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: ['w-full', 'h-full', 'object-cover'],
    containerCarousel: ['-mx-[8px]', 'lg:mx-0'],
    customPaginationDotsWrapper: [
      'flex',
      'gap-spacing-spacing-12',
      'items-center',
      'justify-center',
      'mt-component-section-container-gutter',
    ],
    paginationDots: [],
    dotStyle: [],
  },
  variants: {
    isActiveSlide: {
      true: {
        paginationDots: [
          'w-spacing-spacing-40',
          'h-spacing-spacing-12',
          'flex',
          'justify-center',
          'items-center',
          'rounded-border-radius-radius-4',
          'overflow-hidden',
        ],
        dotStyle: [
          '!w-spacing-spacing-40',
          '!h-spacing-spacing-12',
          'text-color-fill-dark',
          '[stroke-width:1px]',
          'stroke-color-border-dark',
        ],
      },
      false: {
        paginationDots: [
          'w-spacing-spacing-12',
          'h-spacing-spacing-12',
          'flex',
          'justify-center',
          'items-center',
        ],
        dotStyle: [
          '!w-spacing-spacing-12',
          '!h-spacing-spacing-12',
          '[stroke-width:1px]',
          'stroke-color-border-dark',
          'fill-none',
        ],
      },
    },
    textAlignment: {
      Left: {
        base: 'text-left',
      },
      Center: {
        base: 'text-center',
        sectionTitleWrapper: 'justify-center mx-auto',
        sectionContentWrapper: 'justify-center',
        sectionDivider: 'mx-auto',
        buttonWrapper: 'justify-center',
      },
      Right: {
        base: 'text-right',
        sectionTitleWrapper: 'justify-end',
        sectionContentWrapper: 'justify-end',
        sectionDivider: 'ml-auto',
        buttonWrapper: 'justify-end',
      },
    },
    backgroundLayout: {
      FullWidth: {
        base: [
          'before:z-[-1]',
          'before:h-full',
          'before:absolute',
          'before:bg-component-section-bg',
          'before:top-0',
          'before:left-[calc(-50vw+50%)]',
          'before:right-[calc(-50vw+50%)]',
        ],
      },
      Content: {
        base: [],
      },
    },
    hasContent: {
      true: {
        sectionContentWrapper: 'gap-component-section-container-gutter',
      },
      false: {
        sectionContentWrapper: 'gap-0',
      },
    },
    removePaddingTop: {
      true: {
        base: ['pt-0'],
      },
      false: {
        base: ['pt-component-section-container-padding-y'],
      },
    },
    removePaddingBottom: {
      true: {
        base: ['pb-0'],
      },
      false: {
        base: ['pb-component-section-container-padding-y'],
      },
    },
    removePaddingLeftRight: {
      true: {
        base: [],
      },
      false: {
        base: ['px-component-section-container-padding-x'],
      },
    },
    displayCenter75: {
      true: {
        sectionContentWrapper: ['max-w-layout-container-center70-max-width', 'mx-auto'],
      },
      false: {
        sectionContentWrapper: [],
      },
    },
    colCount: {
      '1': {
        card: ['w-[calc((100%-var(--layout-base-gutter))/2)]'],
        header: ['aspect-[135/76]'],
        imageWrapper: [],
        container: ['h-full', 'flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
      },
      '2': {
        sectionContentWrapper: ['contentWrapper'],
        card: ['w-[calc((100%-var(--layout-base-gutter))/2)]'],
        header: ['aspect-[135/76]'],
        imageWrapper: [],
        container: ['h-full', 'flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
      },
      '3': {
        card: ['w-[calc((100%-2*var(--layout-base-gutter))/3)]'],
        header: ['aspect-[135/76]'],
        imageWrapper: [],
        container: ['h-full', 'flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
      },
      '4': {
        card: ['w-full', 'lg:w-[calc((100%-3*var(--layout-base-gutter))/4)]'],
        container: ['h-full', 'flex-col'],
        body: ['flex-col', 'items-center', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
        imageWrapper: ['aspect-[135/76]'],
      },
    },
  },
  defaultVariants: {
    textAlignment: 'Center',
    hasContent: true,
    removePaddingTop: false,
    removePaddingBottom: false,
    removePaddingLeftRight: true,
    displayCenter75: false,
    backgroundLayout: 'FullWidth',
    colCount: '2',
  },
});
